'use client'

import clsx from 'clsx'
import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Accordion } from '../Accordion'
import { Checkbox } from '../Checkbox'
import { Link } from '../Link'

type SelectionContextValue = {
  selectable: boolean
  selectedIds: Set<string>
  onSelectionChange: (ids: Set<string>) => void
  rowIds: Set<string>
  registerRowId: (id: string) => void
  unregisterRowId: (id: string) => void
}

const SelectionContext = createContext<SelectionContextValue | null>(null)

const TableContext = createContext<{ dense: boolean; grid: boolean; striped: boolean }>({
  dense: false,
  grid: false,
  striped: false,
})

export type TableSelectionProps = {
  selectable?: boolean
  selectedRowIds?: string[]
  onSelectionChange?: (ids: string[]) => void
}

export const Table = ({
  dense = false,
  grid = false,
  striped = false,
  selectable = false,
  selectedRowIds = [],
  onSelectionChange,
  className,
  children,
  ...props
}: TableSelectionProps & {
  dense?: boolean
  grid?: boolean
  striped?: boolean
} & React.ComponentPropsWithoutRef<'div'>) => {
  const [rowIds, setRowIds] = useState<Set<string>>(new Set())
  const selectedIds = useMemo(() => new Set(selectedRowIds), [selectedRowIds])

  const registerRowId = useCallback((id: string) => {
    setRowIds((prev) => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }, [])

  const unregisterRowId = useCallback((id: string) => {
    setRowIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }, [])

  const handleSelectionChange = useCallback(
    (ids: Set<string>) => {
      onSelectionChange?.(Array.from(ids))
    },
    [onSelectionChange],
  )

  const selectionValue: SelectionContextValue | null =
    selectable && onSelectionChange
      ? {
          selectable: true,
          selectedIds,
          onSelectionChange: handleSelectionChange,
          rowIds,
          registerRowId,
          unregisterRowId,
        }
      : null

  return (
    <SelectionContext.Provider value={selectionValue}>
      <TableContext.Provider value={{ dense, grid, striped } as React.ContextType<typeof TableContext>}>
        <div className="flow-root">
          <div
            {...props}
            className={clsx(className, '-mx-(--gutter) overflow-x-auto whitespace-nowrap')}
          >
            <div
              className={clsx(
                'inline-block min-w-full align-middle overflow-clip border border-gray-200 dark:border-gray-700 rounded-lg',
              )}
            >
              <table className="min-w-full text-left text-sm/6 text-gray-900 dark:text-white">{children}</table>
            </div>
          </div>
        </div>
      </TableContext.Provider>
    </SelectionContext.Provider>
  )
}

export const TableHead = ({ className, ...props }: React.ComponentPropsWithoutRef<'thead'>) => {
  return (
    <thead
      {...props}
      className={clsx(className, 'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800')}
    />
  )
}

export const TableBody = (props: React.ComponentPropsWithoutRef<'tbody'>) => {
  return <tbody {...props} />
}

const TableRowContext = createContext<{ to?: string; target?: string; title?: string; rowId?: string }>({
  to: undefined,
  target: undefined,
  title: undefined,
  rowId: undefined,
})

export const TableRow = ({
  to,
  onClick,
  target,
  title,
  rowId,
  className,
  ...props
}: {
  to?: string
  onClick?: () => void
  target?: string
  title?: string
  rowId?: string
} & React.ComponentPropsWithoutRef<'tr'>) => {
  const { striped } = useContext(TableContext)
  const selection = useContext(SelectionContext)
  const selectionRef = useRef(selection)
  selectionRef.current = selection

  useEffect(() => {
    if (!rowId) {
      return
    }

    const sel = selectionRef.current
    if (!sel?.selectable) {
      return
    }

    sel.registerRowId(rowId)
    return () => sel.unregisterRowId(rowId)
  }, [rowId])

  return (
    <TableRowContext.Provider value={{ to, target, title, rowId } as React.ContextType<typeof TableRowContext>}>
      <tr
        {...props}
        onClick={onClick}
        className={clsx(
          className,
          (to || onClick) &&
            'cursor-pointer has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-primary-500 dark:focus-within:bg-white/[2.5%]',
          striped && 'even:bg-gray-900/[2.5%] dark:even:bg-white/[2.5%]',
          (to || onClick) && striped && 'hover:bg-gray-900/5 dark:hover:bg-white/5',
          (to || onClick) && !striped && 'hover:bg-gray-900/[2.5%] dark:hover:bg-white/[2.5%]',
        )}
      />
    </TableRowContext.Provider>
  )
}

export const TableHeader = ({ className, ...props }: React.ComponentPropsWithoutRef<'th'>) => {
  const { grid } = useContext(TableContext)
  return (
    <th
      {...props}
      className={clsx(
        className,
        'border-b border-b-gray-900/10 px-4 py-2 font-medium dark:border-b-white/10',
        grid && 'border-l border-l-gray-900/5 first:border-l-0 dark:border-l-white/5',
      )}
    />
  )
}

/**
 * Checkbox for the table header to select or deselect all selectable rows.
 * Only renders when the table has selectable bulk actions enabled.
 */
export const TableSelectAllCheckbox = () => {
  const selection = useContext(SelectionContext)
  if (!selection?.selectable) {
    return null
  }

  const { selectedIds, onSelectionChange, rowIds } = selection
  const allIds = Array.from(rowIds)
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id))
  const someSelected = selectedIds.size > 0

  const handleChange = (checked: boolean) => {
    if (checked) {
      onSelectionChange(new Set(allIds))
    } else {
      onSelectionChange(new Set())
    }
  }

  return (
    <span
      onClick={(e) => e.stopPropagation()}
      className="inline-flex"
    >
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected && !allSelected}
        onChange={handleChange}
        aria-label={allSelected ? 'Deselect all rows' : 'Select all rows'}
      />
    </span>
  )
}

/**
 * Checkbox for a single row. Use with TableRow rowId when the table has selectable bulk actions.
 */
export const TableRowCheckbox = ({ rowId }: { rowId: string }) => {
  const selection = useContext(SelectionContext)
  if (!selection?.selectable) {
    return null
  }

  const { selectedIds, onSelectionChange } = selection
  const checked = selectedIds.has(rowId)

  const handleChange = (checked: boolean) => {
    const next = new Set(selectedIds)
    if (checked) {
      next.add(rowId)
    } else {
      next.delete(rowId)
    }

    onSelectionChange(next)
  }

  return (
    <span
      onClick={(e) => e.stopPropagation()}
      className="inline-flex"
    >
      <Checkbox
        checked={checked}
        onChange={handleChange}
        aria-label={checked ? `Deselect row ${rowId}` : `Select row ${rowId}`}
      />
    </span>
  )
}

export const TableCell = ({ className, children, ...props }: React.ComponentPropsWithoutRef<'td'>) => {
  const { dense, grid, striped } = useContext(TableContext)
  const { to, target, title } = useContext(TableRowContext)
  const [cellRef, setCellRef] = useState<HTMLElement | null>(null)

  return (
    <td
      ref={to ? setCellRef : undefined}
      {...props}
      className={clsx(
        className,
        'relative px-4',
        !striped && 'border-b border-gray-900/5 dark:border-white/5',
        grid && 'border-l border-l-gray-900/5 first:border-l-0 dark:border-l-white/5',
        dense ? 'py-2.5' : 'py-4',
      )}
    >
      {to && (
        <Link
          data-row-link
          to={to}
          target={target}
          aria-label={title}
          tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
          className="absolute inset-0 focus:outline-hidden"
        />
      )}
      {children}
    </td>
  )
}

type TableExpandableRowProps = {
  children: React.ReactNode
  colSpan: number
  details: React.ReactNode
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  className?: string
  detailsClassName?: string
}

/**
 * Expandable table row: trigger row (cells) + details row. Uses Accordion so the row is the trigger.
 * Click the row to expand; use stopPropagation on cells that must not toggle (e.g. Actions).
 * Details row stays in the DOM so the close animation can run; after it ends we collapse it so it takes no space.
 */
export const TableExpandableRow = ({
  children,
  className,
  colSpan,
  details,
  detailsClassName,
  expanded,
  onExpandedChange,
}: TableExpandableRowProps) => {
  return (
    <Accordion
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      panelWrapper={({ panel, isExpanded }) => (
        <TableRow aria-hidden={!isExpanded}>
          <TableCell
            colSpan={colSpan}
            className={clsx(
              'transition-all duration-200 ease-in-out align-top border-b border-gray-900/5 dark:border-white/5',
              !isExpanded && 'border-b-0 py-0!',
              detailsClassName,
            )}
          >
            {panel}
          </TableCell>
        </TableRow>
      )}
      trigger={({ triggerProps }) => (
        <TableRow
          {...triggerProps}
          className={clsx('cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50', className)}
        >
          {children}
        </TableRow>
      )}
    >
      {details}
    </Accordion>
  )
}
